import { useEffect, useMemo, useState } from "react";
import {
  Users as UsersIcon,
  Plus,
  UserCheck,
  UserX,
  Building2,
  FileText,
  Link as LinkIcon,
  Pencil,
  AlertCircle,
  Trash2,
  AlertTriangle,
  Loader2,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, ROLES, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isDiretora = currentUser?.role === ROLES.DIRETORA;
  // Secretária pode visualizar a lista e editar VÍNCULOS de CLIENTE, mas não
  // pode criar/excluir/desativar usuários (Melhoria 5).
  const canManageFullUsers = isDiretora;
  const canManageClientLinks = isDiretora || currentUser?.role === ROLES.SECRETARIA;

  const [users, setUsers] = useState(null);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, client_id, contract_id} when editing
  const [deleting, setDeleting] = useState(null); // user object being deleted
  const [deletingBusy, setDeletingBusy] = useState(false);
  // M19 (2026-06-17): state para reset de senha
  const [resettingUser, setResettingUser] = useState(null);
  const [resetForm, setResetForm] = useState({ new_password: "", confirm: "", show: false });
  const [resettingBusy, setResettingBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: ROLES.CONSULTOR,
    client_id: "",
    contract_ids: [], // Melhoria 5: array em vez de string
  });

  const load = async () => {
    const [u, c, k] = await Promise.all([
      api.get("/users"),
      api.get("/clients"),
      api.get("/contracts"),
    ]);
    setUsers(u.data);
    setClients(c.data);
    setContracts(k.data);
  };

  useEffect(() => {
    load();
  }, []);

  // Contracts filtered by selected client (município)
  const contractsForClient = useMemo(() => {
    if (!form.client_id) return [];
    return contracts.filter((c) => c.client_id === form.client_id);
  }, [contracts, form.client_id]);

  const create = async () => {
    if (!form.name || !form.email || !form.password) {
      return toast.error("Preencha nome, e-mail e senha.");
    }
    if (form.role === ROLES.CLIENTE) {
      if (!form.client_id)
        return toast.error("Selecione o município (cliente) deste login.");
      if (!form.contract_ids || form.contract_ids.length === 0)
        return toast.error("Selecione pelo menos um contrato/produto.");
    }
    try {
      await api.post("/users", {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
        client_id: form.role === ROLES.CLIENTE ? form.client_id : null,
        contract_ids: form.role === ROLES.CLIENTE ? form.contract_ids : null,
      });
      toast.success("Usuário criado.");
      setForm({
        name: "",
        email: "",
        password: "",
        role: ROLES.CONSULTOR,
        client_id: "",
        contract_ids: [],
      });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar usuário.");
    }
  };

  const toggle = async (id, active) => {
    try {
      await api.put(`/users/${id}/toggle`, { active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success(`Usuário ${deleting.name} excluído.`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir.");
    } finally {
      setDeletingBusy(false);
    }
  };

  const openEdit = (u) => {
    // Compat: usa contract_ids se disponível, senão deriva do legacy contract_id
    const ids = (u.contract_ids && u.contract_ids.length > 0)
      ? u.contract_ids
      : (u.contract_id ? [u.contract_id] : []);
    setEditing({
      id: u.id,
      name: u.name,
      email: u.email,
      client_id: u.client_id || "",
      contract_ids: ids,
    });
  };

  const editContractsForClient = useMemo(() => {
    if (!editing?.client_id) return [];
    return contracts.filter((c) => c.client_id === editing.client_id);
  }, [contracts, editing?.client_id]);

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.client_id)
      return toast.error("Selecione o município (cliente).");
    if (!editing.contract_ids || editing.contract_ids.length === 0)
      return toast.error("Selecione pelo menos um contrato.");
    try {
      await api.put(`/users/${editing.id}/client-link`, {
        client_id: editing.client_id,
        contract_ids: editing.contract_ids,
      });
      toast.success("Vínculo atualizado.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar.");
    }
  };

  // M19 (2026-06-17): resetar senha (Diretora + Secretária)
  const openReset = (u) => {
    setResetForm({ new_password: "", confirm: "", show: false });
    setResettingUser(u);
  };

  const submitReset = async () => {
    if (!resettingUser) return;
    if (resetForm.new_password.length < 6) {
      return toast.error("A senha deve ter pelo menos 6 caracteres.");
    }
    if (resetForm.new_password !== resetForm.confirm) {
      return toast.error("A confirmação não confere com a nova senha.");
    }
    setResettingBusy(true);
    try {
      await api.post(`/users/${resettingUser.id}/reset-password`, {
        new_password: resetForm.new_password,
      });
      toast.success(
        `Senha redefinida. ${resettingUser.name} terá que criar uma nova senha no próximo login.`,
      );
      setResettingUser(null);
      setResetForm({ new_password: "", confirm: "", show: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao redefinir senha.");
    } finally {
      setResettingBusy(false);
    }
  };

  const clientName = (cid) =>
    clients.find((c) => c.id === cid)?.name || null;
  const contractLabel = (kid) => {
    const k = contracts.find((c) => c.id === kid);
    if (!k) return null;
    return k.product_name || k.id.slice(0, 8);
  };

  if (!users) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-midnight" /> Usuários
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Cadastre e gerencie usuários por perfil. Logins de{" "}
            <strong>CLIENTE</strong> acessam apenas o contrato específico ao
            qual estão vinculados.
          </p>
        </div>
        {canManageFullUsers && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-midnight hover:bg-ink text-white"
                data-testid="new-user-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Novo usuário
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Novo usuário</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label>Senha inicial *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label>Perfil</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) =>
                    setForm({ ...form, role: v, client_id: "", contract_id: "" })
                  }
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.role === ROLES.CLIENTE && (
                <>
                  <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-md text-xs text-slate-700">
                    <AlertCircle className="inline w-3.5 h-3.5 mr-1 text-blue-600" />
                    Este login terá acesso <strong>apenas</strong> ao
                    contrato/produto selecionado abaixo.
                  </div>
                  <div>
                    <Label>Município (Cliente) *</Label>
                    <Select
                      value={form.client_id}
                      onValueChange={(v) =>
                        setForm({ ...form, client_id: v, contract_id: "" })
                      }
                    >
                      <SelectTrigger data-testid="user-client-select">
                        <SelectValue placeholder="Selecione o município…" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Contratos / Produtos *</Label>
                    <ContractMultiPicker
                      selectedIds={form.contract_ids}
                      onChange={(ids) =>
                        setForm({ ...form, contract_ids: ids })
                      }
                      availableContracts={contractsForClient}
                      disabled={!form.client_id}
                      testIdPrefix="user"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Selecione um ou mais contratos. Cliente verá apenas os contratos vinculados.
                    </p>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={create}
                className="bg-midnight hover:bg-ink text-white"
                data-testid="user-create-submit"
              >
                Criar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <Card className="card-soft overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-medium">Nome</th>
                <th className="px-4 py-3 font-medium">E-mail</th>
                <th className="px-4 py-3 font-medium">Perfil</th>
                <th className="px-4 py-3 font-medium">Vínculo</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b border-slate-100"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {u.name}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <Badge
                      variant="outline"
                      className="border-slate-200 text-xs"
                    >
                      {ROLE_LABEL[u.role]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {u.role === ROLES.CLIENTE ? (
                      (() => {
                        const ids = (u.contract_ids && u.contract_ids.length > 0)
                          ? u.contract_ids
                          : (u.contract_id ? [u.contract_id] : []);
                        if (ids.length === 0) {
                          if (u.client_id) {
                            return (
                              <div className="inline-flex items-center gap-1 text-amber-700">
                                <AlertCircle className="w-3.5 h-3.5" />
                                <span>
                                  Apenas município ({clientName(u.client_id) || "—"})
                                  — atribuir contrato
                                </span>
                              </div>
                            );
                          }
                          return <span className="text-red-600">Sem vínculo</span>;
                        }
                        return (
                          <div className="space-y-0.5">
                            <div className="inline-flex items-center gap-1 text-slate-700">
                              <Building2 className="w-3 h-3" />
                              {clientName(u.client_id) || "—"}
                            </div>
                            <div className="space-y-0.5">
                              {ids.map((cid) => (
                                <div
                                  key={cid}
                                  className="inline-flex items-center gap-1 text-slate-900 font-medium mr-2"
                                >
                                  <FileText className="w-3 h-3" />
                                  {contractLabel(cid) || cid.slice(0, 8)}
                                </div>
                              ))}
                            </div>
                            {ids.length > 1 && (
                              <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                                {ids.length} contratos
                              </span>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.active ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Ativo
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                        Inativo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      {u.role === ROLES.CLIENTE && canManageClientLinks && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(u)}
                          data-testid={`user-edit-link-${u.id}`}
                        >
                          <LinkIcon className="w-3.5 h-3.5 mr-1" />
                          Vínculo
                        </Button>
                      )}
                      {/* M19: reset de senha — Diretora ou Secretária */}
                      {canManageClientLinks && u.id !== currentUser?.id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReset(u)}
                          className="border-sky-200 text-sky-800 hover:bg-sky-50"
                          data-testid={`user-reset-password-${u.id}`}
                          title="Redefinir senha do usuário"
                        >
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Nova senha
                        </Button>
                      )}
                      {canManageFullUsers && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggle(u.id, !u.active)}
                            data-testid={`user-toggle-${u.id}`}
                          >
                            {u.active ? (
                              <>
                                <UserX className="w-3.5 h-3.5 mr-1" /> Desativar
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-3.5 h-3.5 mr-1" /> Ativar
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleting(u)}
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            data-testid={`user-delete-${u.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    Nenhum usuário cadastrado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit dialog for CLIENTE link */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Editar vínculo do CLIENTE
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <strong>{editing.name}</strong> ({editing.email})
              </div>
              <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-md text-xs text-slate-700">
                <AlertCircle className="inline w-3.5 h-3.5 mr-1 text-blue-600" />
                Atribua o <strong>contrato específico</strong> para que este
                login enxergue <strong>apenas</strong> as informações daquele
                produto.
              </div>
              <div>
                <Label>Município (Cliente) *</Label>
                <Select
                  value={editing.client_id}
                  onValueChange={(v) =>
                    setEditing({ ...editing, client_id: v, contract_ids: [] })
                  }
                >
                  <SelectTrigger data-testid="edit-client-select">
                    <SelectValue placeholder="Selecione…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contratos / Produtos vinculados *</Label>
                <ContractMultiPicker
                  selectedIds={editing.contract_ids || []}
                  onChange={(ids) =>
                    setEditing({ ...editing, contract_ids: ids })
                  }
                  availableContracts={editContractsForClient}
                  disabled={!editing.client_id}
                  testIdPrefix="edit"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  O cliente verá apenas os contratos selecionados. Vínculos múltiplos
                  são úteis quando a mesma pessoa representa o município em mais de
                  uma consultoria (ex.: Regimento Interno + Lei Orgânica).
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              className="bg-midnight hover:bg-ink text-white"
              data-testid="edit-save-link"
            >
              Salvar vínculo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation dialog */}
      <Dialog open={!!deleting} onOpenChange={(v) => !deletingBusy && !v && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Excluir usuário
            </DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-slate-800">
                <strong>Tem certeza que deseja excluir este usuário?</strong>
                <br />
                <span className="text-red-800">Esta ação não pode ser desfeita.</span>
              </div>
              <div className="text-sm text-slate-700">
                <div><strong>Nome:</strong> {deleting.name}</div>
                <div><strong>E-mail:</strong> {deleting.email}</div>
                <div><strong>Perfil:</strong> {ROLE_LABEL[deleting.role]}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={deletingBusy}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deletingBusy}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="user-delete-confirm"
            >
              {deletingBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* M19: Modal de reset de senha */}
      <Dialog
        open={!!resettingUser}
        onOpenChange={(v) => !resettingBusy && !v && setResettingUser(null)}
      >
        <DialogContent className="max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-sky-800" />
              Redefinir senha
            </DialogTitle>
            <DialogDescription>
              Defina uma senha temporária para{" "}
              <strong>{resettingUser?.name}</strong> ({resettingUser?.email}).
              O usuário será obrigado a criar uma nova senha pessoal no próximo login.
              <br />
              <span className="text-xs text-slate-500">
                Esta ação altera apenas a senha — não modifica perfil,
                vínculos, contratos ou acessos.
              </span>
            </DialogDescription>
          </DialogHeader>
          {resettingUser && (
            <div className="space-y-3">
              <div>
                <Label>Nova senha temporária</Label>
                <div className="relative mt-1">
                  <Input
                    type={resetForm.show ? "text" : "password"}
                    value={resetForm.new_password}
                    onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                    placeholder="mínimo 6 caracteres"
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setResetForm({ ...resetForm, show: !resetForm.show })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    data-testid="reset-password-toggle-visibility"
                  >
                    {resetForm.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirme a senha</Label>
                <Input
                  type={resetForm.show ? "text" : "password"}
                  value={resetForm.confirm}
                  onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                  className="mt-1"
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <div className="p-2 bg-sky-50 border border-sky-200 rounded text-xs text-sky-900">
                <strong>Instrução para o usuário:</strong> após entrar com esta senha temporária,
                ele será direcionado automaticamente para criar uma nova senha.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResettingUser(null)}
              disabled={resettingBusy}
            >
              Cancelar
            </Button>
            <Button
              onClick={submitReset}
              disabled={resettingBusy}
              className="bg-sky-800 hover:bg-sky-900 text-white"
              data-testid="reset-password-submit"
            >
              {resettingBusy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4 mr-2" />
              )}
              Redefinir senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Multi-contract picker (Melhoria 5, 2026-06-12)
// ---------------------------------------------------------------------------
// Padrão "chips + add button" para selecionar 1+ contratos.
// Os contratos disponíveis são filtrados externamente (ex.: pelo município).
function ContractMultiPicker({
  selectedIds,
  onChange,
  availableContracts,
  disabled = false,
  testIdPrefix = "contracts",
}) {
  const [pickValue, setPickValue] = useState("");

  const idToContract = useMemo(() => {
    const map = new Map();
    for (const c of availableContracts) map.set(c.id, c);
    return map;
  }, [availableContracts]);

  // Contratos restantes para o dropdown (exclui já selecionados)
  const remaining = useMemo(
    () =>
      availableContracts.filter((c) => !selectedIds.includes(c.id)),
    [availableContracts, selectedIds]
  );

  const handleAdd = (id) => {
    if (!id || selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setPickValue("");
  };

  const handleRemove = (id) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-multipicker`}>
      {/* Lista de selecionados (chips) */}
      {selectedIds.length === 0 ? (
        <div className="text-xs text-slate-500 italic px-2 py-1 border border-dashed border-slate-300 rounded-md">
          {disabled
            ? "Selecione o município primeiro"
            : "Nenhum contrato vinculado ainda — adicione abaixo."}
        </div>
      ) : (
        <ul className="space-y-1" data-testid={`${testIdPrefix}-selected-list`}>
          {selectedIds.map((cid) => {
            const c = idToContract.get(cid);
            return (
              <li
                key={cid}
                className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm"
                data-testid={`${testIdPrefix}-selected-${cid}`}
              >
                <span className="inline-flex items-center gap-1.5 text-slate-800 flex-1 min-w-0">
                  <FileText className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                  <span className="truncate">
                    {c?.product_name || cid.slice(0, 8)}
                  </span>
                </span>
                {!disabled && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(cid)}
                    className="h-7 w-7 text-red-600 hover:bg-red-50"
                    aria-label="Remover contrato"
                    title="Remover contrato"
                    data-testid={`${testIdPrefix}-remove-${cid}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add button */}
      {!disabled && remaining.length > 0 && (
        <div className="flex gap-2">
          <Select value={pickValue} onValueChange={setPickValue}>
            <SelectTrigger
              className="flex-1"
              data-testid={`${testIdPrefix}-picker-trigger`}
            >
              <SelectValue placeholder="Selecione um contrato disponível…" />
            </SelectTrigger>
            <SelectContent>
              {remaining.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.product_name || c.id.slice(0, 8)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            onClick={() => handleAdd(pickValue)}
            disabled={!pickValue}
            className="bg-midnight hover:bg-ink text-white"
            data-testid={`${testIdPrefix}-add-button`}
          >
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
        </div>
      )}

      {!disabled && remaining.length === 0 && selectedIds.length > 0 && (
        <p className="text-[10px] text-slate-500 italic">
          Todos os contratos deste município já foram vinculados.
        </p>
      )}
    </div>
  );
}
